import request from '@/utils/request.js'
import { ossUploadType } from '@/utils/constants'
import OSS from 'ali-oss'
import axios from 'axios'

// 全局缓存，避免每次都拿 STS
let stsCache = null
const STS_EXPIRE_SEC = 300 // 提前 5 分钟刷新

// 获取 STS 临时凭证
async function getSTSToken(uploadType) {
  // 缓存未过期直接返回
  if (stsCache && stsCache.expire - Date.now() > STS_EXPIRE_SEC * 1000) {
    return stsCache
  }

  // 修改这里：使用 request.get 而不是直接调用 request
  const { data } = await getSts(uploadType)
  console.log(data)
  // 后端约定返回：{ accessKeyId, accessKeySecret, stsToken, expire,bucket,cdn, uploadDir,endpoint}
  stsCache = { ...data, expire: data.expire * 1000 }
  return stsCache
}

// 生成随机文件名（保留原后缀）
function genKey(file, uploadDir) {
  console.log(file)
  const ext = file.name.split('.').pop()
  const rand = Date.now().toString(36) + Math.random().toString(36).slice(2, 8)
  return `${uploadDir}/${rand}.${ext}`
}

// 查询上传到阿里oss的凭证 - 修改这里
export function getSts(uploadType) {
  // 使用 request.get 方法
  return request.get('/v1/aliyunOss/getStsToken?uploadType=' + uploadType)
}

/**
 * 上传主函数
 * @param {number} uploadType  上传类型：0-通用图片 1-主题图片
 * @param {File} file      原生 File 对象
 * @param {Object} options 可选配置
 *        - onProgress : (p: 0~100) => void
 *        - onRetry    : (count: number) => void
 *        - maxRetry   : 默认 2
 * @returns {Promise<string>}  成功返回 oss 访问 url
 */
export async function uploadToOSS(uploadType, file, options = {}) {
  const { onProgress, onRetry, maxRetry = 2 } = options

  // 1. 拿到 STS
  const sts = await getSTSToken(uploadType)

  // 2. 实例化 OSS Client
  const client = new OSS({
    region: sts.region,
    accessKeyId: sts.accessKeyId,
    accessKeySecret: sts.accessKeySecret,
    bucket: sts.bucket,
    refreshSTSToken: async () => {
      const fresh = await getSTSToken()
      return {
        accessKeyId: fresh.accessKeyId,
        accessKeySecret: fresh.accessKeySecret
      }
    },
    // 刷新阈值（秒）
    refreshSTSTokenInterval: 300
  })

  // 3. 组装参数
  const key = genKey(file, sts.uploadDir)
  const headers = { 'x-oss-forbid-overwrite': 'false' }

  // 4. 上传（支持重试）
  let retry = 0
  while (retry <= maxRetry) {
    try {
      const res = await client.multipartUpload(key, file, {
        headers,
        progress: (p, cpt) => {
          // p: 0~1
          onProgress && onProgress(Math.round(p * 100))
        },
        // 分片大小 2 MB
        partSize: 1024 * 1024 * 2,
        // 并发 3
        parallel: 3
      })
      // 返回可访问的 url（公共读）
      return `https://${sts.cdn}/${res.name}`
    } catch (e) {
      // 只有网络/超时错误才重试
      if (e.code === 'ConnectionTimeoutError' && retry < maxRetry) {
        retry++
        onRetry && onRetry(retry)
        continue
      }
      // 其它错误直接抛
      throw e
    }
  }
}

// 获取小文件上传到阿里oss的预签名地址 - 修改这里
export function getPreSignUrl(data) {
  return request.post('/v1/aliyunOss/generatePreSignUrl', data)
}

/**
 * 获取大文件上传到阿里oss的多个分片预签名地址
 * @param {*} data
 * @returns {"uploadId":"xxxx","partSignUrls":["https://part1xxxx",""https://part2xxxx",.....}
 */
export function getMultipartPreSignUrl(data) {
  return request.post('/v1/aliyunOss/getMultipartUploadPreSignUrl', data)
}

// 确认和合并分片
export function completedMultipartUpload(data) {
  return request.post('/v1/aliyunOss/completedMultipartUpload', data)
}

// 生成随机文件名（保留原后缀）
function generateFileName(file) {
  console.log('file', file)
  if(file.name){
    return file.name
  }
  if (!file.name) {
    const ext = file.name.split('.').pop()
    const rand = Date.now().toString(36) + Math.random().toString(36).slice(2, 8)
    return `${rand}.${ext}`
  }
}

/**
 * 通过 OSS 预签名 URL 上传文件
 * @param {File} file               // 浏览器<input types="file">拿到的 File 对象
 * @param {String} signedUrl        // 服务端生成的 PUT 签名 URL（含签名参数）
 * @param {Object} [options={}]     // 可选配置
 * @param {Object} [options.headers]        // 需要额外携带的 header（如 Content-Type、x-oss-meta-*）
 * @param {Function} [options.onProgress]   // 进度回调，(percent: 0~100) => {}
 * @returns {Promise<{status:number, data:any}>}  // 成功返回 {status, data}，失败走 catch
 */
export async function uploadBySignedUrl(file, signedUrl, options = {}) {
  const { headers = {}, onProgress } = options

  if (!headers['Content-Type'] && !headers['content-type']) {
    headers['Content-Type'] = file.type || 'image/png'
  }

  return axios
      .put(signedUrl, file, {
        headers,
        timeout: 10000,
        onUploadProgress: (progress) => {
          if (onProgress && progress.total > 0) {
            const percent = Math.round((progress.loaded / progress.total) * 100)
            onProgress(percent)
          }
        }
      })
      .then((res) => ({ status: res.status, data: res.data }))
      .catch((err) => {
        const status = err.response?.status
        const msg = err.response?.data || err.message
        console.error('[alioss] 上传失败，HTTP 状态：', status, '错误详情：', msg)
        return Promise.reject(new Error(`上传失败 (${status || 'network'})：${msg}`))
      })
}

export async function up2OSSByPreSignUrl(uploadType, file, options = {}) {
  const fileName = generateFileName(file)
  const contentType = file.type || 'image/png'
  const data = { uploadType, fileName, contentType }

  const presignRes = await getPreSignUrl(data)

  if (presignRes && presignRes.code === '200') {
    const presignResData = presignRes.data
    return uploadBySignedUrl(file, presignResData.preSignUrl, options)
        .then((res) => {
          console.log('res:', res)
          if (res.status === 200) {
            return presignResData.accessUrl
          } else {
            return Promise.reject(new Error('上传失败'))
          }
        })
        .catch((err) => {
          return Promise.reject(new Error('上传失败'))
        })
  } else {
    return Promise.reject(new Error('请求后台的获取上传接口失败'))
  }
}

/**
 * 大文件分片上传
 * @param {number} uploadType 上传类型
 * @param {File} file 文件对象
 * @param {Object} options 配置项
 * @returns {Promise<string>} 上传成功后的文件URL
 */
export async function uploadLargeFileByParts(uploadType, file, options = {}) {
  const { customFileName, onProgress, onRetry, maxRetry = 3 } = options
  const CHUNK_SIZE = 5 * 1024 * 1024 // 5MB每片
  let uploadedChunks = 0

  // 1. 处理文件名
  let fileName
  if (customFileName) {
    const hasChinese = /[\u4e00-\u9fa5]/.test(customFileName)
    if (hasChinese) {
      return Promise.reject(new Error('文件名包含中文，不允许上传'))
    }
    fileName = customFileName
  } else {
    fileName = generateFileName(file)
  }
  const contentType = file.type || 'application/octet-stream'

  // 2. 获取分片上传ID和预签名URL
  const multipartData = {
    uploadType,
    fileName,
    contentType,
    fileSize: file.size,
    partSize: CHUNK_SIZE
  }

  const multipartRes = await getMultipartPreSignUrl(multipartData)
  if (!multipartRes || multipartRes.code !== '200') {
    return Promise.reject(new Error('获取分片上传参数失败'))
  }

  if (!multipartRes.data || !multipartRes.data.uploadId || !multipartRes.data.partSignUrls) {
    return Promise.reject(new Error('获取分片上传参数失败'))
  }

  const { uploadId, partSignUrls, accessUrl } = multipartRes.data

  if (partSignUrls.length === 0) {
    return Promise.reject(new Error('获取分片上传参数失败'))
  }

  // 3. 分片上传
  const uploadPromises = []

  const totalChunks = partSignUrls.length

  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_SIZE
    const end = Math.min(start + CHUNK_SIZE, file.size)
    const chunk = file.slice(start, end)
    const partNumber = i + 1
    const signedUrl = partSignUrls[i]

    const uploadChunk = async () => {
      let retry = 0
      try {
        await axios.put(signedUrl, chunk, {
          headers: {
            'Content-Type': contentType
          },
          onUploadProgress: (progress) => {
            const chunkProgress = progress.loaded / progress.total
            const overallProgress = ((uploadedChunks + chunkProgress) / totalChunks) * 100
            onProgress && onProgress(Math.round(overallProgress))
          },
          timeout: 120000
        })

        uploadedChunks++
        if (uploadedChunks === totalChunks) {
          onProgress && onProgress(100)
        }

        return { partNumber, etag: '' }
      } catch (error) {
        if (onRetry && retry < maxRetry) {
          retry++
          onRetry(partNumber, retry)
          return uploadChunk()
        }
        throw error
      }
    }

    uploadPromises.push(uploadChunk())
  }

  // 4. 并发上传所有分片
  const parts = await Promise.all(uploadPromises)

  // 5. 通知服务器合并分片
  const completeData = {
    uploadType,
    fileName,
    uploadId,
    parts: parts.map((part) => ({
      partNumber: part.partNumber,
      etag: part.etag
    }))
  }

  const completeRes = await completedMultipartUpload(completeData)
  if (!completeRes || completeRes.code !== '200') {
    throw new Error('合并分片失败')
  }

  return accessUrl
}